import {
  useReducer, useMemo, useEffect, useState,
} from 'preact/hooks';
import PropTypes from 'prop-types';
import { signupReducer, signupInitialState } from '../store/signup-reducer';
import Header from './header';

import StoreContext from '../helpers/context';
import { pushCustomEventToDataLayer, trackPageView } from '../helpers/analytics';
import Title from '../contact-info-section/title-section';

import { brandClassName, getBrandInfo } from '../helpers/brands';
import { SIGNUP_TYPES } from '../utils/common-literals';
import getSessionInfo, { isMobileScreen } from '../helpers/session-Info';
import SocialLogin from '../contact-info-section/social-login';
import ContactForm from '../contact-info-section/contact-form';
import TermsSection from '../payment-section/terms-section';
import { MailIcon } from './icons/social-icons';
import {
  DashboardIcon,
  DeskVideoIcon,
  PaymentTabIcon,
  EmailSendIcon,
} from './icons/setmore-icons';
import Submit from '../payment-section/free-signup-section';
import { getUserSignupSource } from '../helpers/common-helper';

const brandInfo = getBrandInfo();

const featuresData = [
  {
    id: 1,
    icon: <DashboardIcon />,
    title: 'Put your stamp on it',
    description:
      "Personalize your Booking Page's URL and feature your logo, brand colors, and reviews.",
  },
  {
    id: 2,
    icon: <DeskVideoIcon />,
    title: 'Make the world your stage',
    description:
      'Offer your services online with <a class="tc-action" href="https://www.setmore.com/integrations/google-meet">Google Meet</a> or <a class="tc-action" href="https://www.setmore.com/integrations/zoom">Zoom</a> video meetings.',
  },
  {
    id: 3,
    icon: <PaymentTabIcon />,
    title: 'Take advance payments',
    description:
      'Get paid through your Booking Page ahead of appointments and events.',
  },
  {
    id: 4,
    icon: <EmailSendIcon />,
    title: 'So long, no-shows',
    description:
      'Automate custom email or text reminders for every client in your calendar.',
  },
];

export default function Signup() {
  const [state, dispatch] = useReducer(signupReducer, signupInitialState);

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  const {
    brand: {
      socialLogin: { facebook, google },
    },
  } = getSessionInfo();

  const [isMobile] = useState(isMobileScreen());
  const [throughEmail, setthroughEmail] = useState(
    !!state?.contact?.email?.value,
  );

  const continueWithEmailClickHandler = () => {
    setthroughEmail(!throughEmail);
    pushCustomEventToDataLayer({
      event: 'signup-email',
      data: {
        source: getUserSignupSource(),
        timestamp: new Date().getTime(),
      },
    });
  };

  useEffect(() => {
    trackPageView({ pageName: 'Free signup' });
  }, []);

  return (
    <StoreContext.Provider value={contextValue}>
      <div
        className={`container-wrapper ${brandClassName}-theme ${
          state.loader ? 'no-user-interaction' : ''
        } ${state.modalToBeShown.view ? 'no-scroll' : ''}`}
      >
        <Header />
        <main>
          <article className="container fx-hd fx-c-nd">
            <section className="left-wrapper">
              <div className="content-wrapper">
                <h1 className="heading heading-lg mb-4-d mb-3">
                  Create your own booking calendar
                </h1>
                <p className="text text-md phone-hide">
                  Schedule appointments, manage your calendar and accept
                  payments Anywhere, with free online booking software.
                </p>
              </div>
              {!isMobile && (
                <picture>
                  <source
                    srcSet="https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar@2x.webp 2x"
                    type="image/webp"
                  />
                  <source
                    srcSet="https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar.png, https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar@2x.png 2x"
                    type="image/png"
                  />
                  <img
                    src="https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar.png"
                    alt="Clients smiling using setmore"
                    width="460"
                    height="256"
                  />
                </picture>
              )}
            </section>
            <section className="right-wrapper">
              <div className="card-content">
                {throughEmail ? (
                  <div className="is-through-email" data-testid="signupForm">
                    <form onSubmit={e => e.preventDefault()}>
                      <ContactForm signupType={SIGNUP_TYPES.FREE_SIGNUP} />
                      <Submit />
                    </form>
                    <div className="or-divider fx-vc">or</div>
                    {(facebook || google) && (
                      <SocialLogin signupType={SIGNUP_TYPES.FREE_SIGNUP} />
                    )}
                  </div>
                ) : (
                  <div data-testid="socialLoginForm">
                    <Title signupType={SIGNUP_TYPES.FREE_SIGNUP} />
                    {(facebook || google) && (
                      <SocialLogin signupType={SIGNUP_TYPES.FREE_SIGNUP} />
                    )}
                    <div className="or-divider fx-vc">or</div>
                    <div className="email-click fx-v">
                      <button
                        className="btn btn-ghost text-sm fx-vc"
                        onClick={continueWithEmailClickHandler}
                        data-testid="continueWithEmailBtn"
                        data-click-id="signup-email"
                      >
                        <MailIcon />
                        <span className="text text-sm">
                          Continue with email
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                <LoginHint throughEmail={throughEmail} />
              </div>
              <TermsSection />
            </section>
            {isMobile && (
              <picture data-testid="mobileImage">
                <source
                  srcSet="https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar-mob@2x.webp 2x"
                  type="image/webp"
                />
                <source
                  srcSet="https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar-mob.png, https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar-mob@2x.png 2x"
                  type="image/png"
                />
                <img
                  src="https://assets.setmore.com/website/v2/images/start-now/setmore-clients-booking-calendar-mob.png"
                  alt="Clients smiling using setmore"
                  height="256"
                  className="img-fit"
                />
              </picture>
            )}
          </article>
          <section className="features">
            <ul className="features__ul container fx">
              {featuresData.map(feature => (
                <li className={`features__li ${isMobile ? 'mb-5' : ''}`} key={feature.id}>
                  {feature.icon}
                  <h3 className="features__title mt-4 mb-3 bold">
                    {feature.title}
                  </h3>
                  <p
                    className="features__content"
                    dangerouslySetInnerHTML={{ __html: feature.description }}
                  />
                </li>
              ))}
            </ul>
          </section>
          <footer className="footer">
            <div className="container fx-hd">
              <a href={brandInfo.websiteUrl} className={`${isMobile ? 'mb-5' : ''}`}>
                <img
                  loading="lazy"
                  src="https://assets.setmore.com/website/v2/images/logo/setmore-logo-white.svg"
                  alt="Setmore logo in white color"
                  width="156"
                  height="24"
                />
              </a>
              <p className="footer__text">
                Grow your business with <span className='bold'>Setmore Community</span>
              </p>
            </div>
          </footer>
        </main>
        <div id="modal-wrapper"></div>
      </div>
    </StoreContext.Provider>
  );
}

function LoginHint({ throughEmail }) {
  return (
    <div className="fx-vc text text-sm mb-1 mt-4">
      <span className="mr-2">Already have an account?</span>
      <a
        href={brandInfo.dashboardUrl}
        data-click-id={throughEmail ? 'login-start-now-2' : 'login-start-now'}
        className="tc-action"
      >
        Login
      </a>
    </div>
  );
}

LoginHint.propTypes = {
  throughEmail: PropTypes.bool.isRequired,
};
